/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { processingJobsV2 as ProcessingJobsV2API, stroot } from '@birdi/js-sdk';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const JOBS_TO_SHOW = 4;

type ProcessingJob = {
  id?: string;
  requestConfig?: any;
  name?: string;
  jobName?: string;
  mapName?: string | null;
  state: string;
  working?: boolean;
  runner?: 'v1' | 'v2';
  startTime?: string | null;
};

const STATUS_MAP = {
  'in-progress': { label: 'In progress', percent: 50, color: '#2A7BE4' },
  requested: { label: 'Pending', percent: 10, color: '#2A7BE4' },
  'pending-payment': { label: 'Pending', percent: 10, color: '#2A7BE4' },
  pending: { label: 'Pending', percent: 10, color: '#2A7BE4' },
  'manual-setup': { label: 'In progress', percent: 50, color: '#2A7BE4' },
  tagging: { label: 'In progress', percent: 50, color: '#2A7BE4' },
  complete: { label: 'Complete', percent: 100, color: '#4AD294' },
  released: { label: 'Complete', percent: 100, color: '#4AD294' },
  failed: { label: 'Failed', percent: 75, color: '#FF6B2C' },
};

// A job stays 'pending' while it runs: v1 workers flag `working` when they
// claim it, v3 (runner 'v2') stamps `startTime` when its execution starts.
const displayState = (job: ProcessingJob) => {
  const running = job.working || (job.runner === 'v2' && !!job.startTime);
  return job.state === 'pending' && running ? 'in-progress' : job.state;
};

export const Processing: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [jobs, setJobs] = useState<ProcessingJob[]>([]);
  const [loading, setLoading] = useState(true);

  const isOrganisationAdmin = meta.permissions.includes(
    'organisation:user:create',
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (!meta.organisation?.id) {
          setJobs([]);
          setLoading(false);
          return;
        }
        const res = await ProcessingJobsV2API.getJobsV2(stroot('mool3u'), {
          start: 1,
          limit: JOBS_TO_SHOW,
          organisationId: meta.organisation.id,
        });
        setJobs(res.body?.items || []);
      } catch (e) {
        setJobs([]);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
      id={suppressIntercomAnchor ? undefined : 'processing'}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Processing</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div
          css={css`
            margin-bottom: 1em;
          `}
        >
          Latest processing jobs
        </div>
        {!loading && jobs.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div>
              <strong>Nothing processing just yet:</strong> Once you request
              processing, the queue will update with status and time estimates.
            </div>
            <div
              css={css`
                margin-top: 0.5em;
              `}
            >
              <a
                href="https://help.birdi.io/en/articles/10434238-how-to-request-processing-for-your-maps-in-birdi-2-0"
                css={css`
                  color: ${theme.deepOcean};
                  text-decoration: underline;
                  margin-top: 0.5em;
                `}
                target="_blank"
              >
                View upload guide
              </a>
            </div>
          </div>
        )}
        {!loading && jobs.length > 0 && (
          <div>
            {jobs.map((job) => {
              const state = displayState(job);
              const status = STATUS_MAP[state as keyof typeof STATUS_MAP] || {
                label: state,
                percent: 0,
                color: theme.mono40,
              };
              const mapId = job.requestConfig?.target?.id || null;
              return (
                <div
                  key={job.id}
                  onClick={() => {
                    posthog?.capture('dashboard processing clicked', {
                      jobId: job.id,
                    });
                    mapId && navigate(`/map/next/${mapId}`);
                  }}
                  css={css`
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 0.5em;
                    cursor: ${mapId ? 'pointer' : 'default'};
                    background: transparent;
                    border-radius: 5px;
                    transition: background 0.15s;
                    &:hover {
                      background: ${mapId ? theme.mono20 : 'transparent'};
                    }
                    padding: 0.5em;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                    `}
                  >
                    <span
                      css={css`
                        min-width: 0;
                        overflow-wrap: anywhere;
                      `}
                    >
                      <span
                        css={css`
                          font-weight: 500;
                        `}
                      >
                        {job.name || job.jobName || 'Untitled'}
                      </span>
                      {job.mapName && (
                        <div
                          css={css`
                            font-size: 0.9em;
                            color: ${theme.mono60};
                          `}
                        >
                          {job.mapName}
                        </div>
                      )}
                    </span>
                    <span
                      css={css`
                        flex: 0 0 auto;
                        padding-left: 0.5em;
                      `}
                    >
                      {status.label}
                    </span>
                  </div>
                  <div
                    css={css`
                      margin: 0.5em 0;
                    `}
                  >
                    <div
                      css={css`
                        width: 100%;
                        height: 8px;
                        background: ${theme.mono30};
                        border-radius: 6px;
                        overflow: hidden;
                        position: relative;
                      `}
                    >
                      <div
                        css={css`
                          width: ${status.percent}%;
                          height: 100%;
                          background: ${status.color};
                          border-radius: 6px;
                          transition: width 0.3s;
                        `}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
            `}
          >
            Loading...
          </div>
        )}
      </div>
      <div
        css={css`
          padding: 1em;
        `}
      >
        <button
          disabled={!isOrganisationAdmin}
          onClick={() => navigate(`/admin-processing`)}
          css={css`
            width: 100%;
            color: ${!isOrganisationAdmin ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 5px;
            padding: 0.5em;
            font-weight: 500;
            cursor: not-allowed;
            cursor: ${!isOrganisationAdmin ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
          `}
        >
          View processing dashboard
        </button>
      </div>
    </div>
  );
};
